import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { ReactComponent as SvgEnvelope } from '@img/icons/email-icon.svg';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import { rgba } from 'polished';
import styled from 'styled-components';
import RichTextStyles from '../RichText/RichText.styles';
import SiteWideStyles from '@stories/Components/Global/SiteWide/SiteWide.styles';
const Container = styled(SectionWrapper) `
  ${setCssVar('accentColor', brand.htaRebrand.greenBright)};
  ${setCssVar('bgColor', brand.htaRebrand.green)};
  ${setCssVar('bgColorAlt', brand.htaRebrand.green)};
  ${setCssVar('fgColor', brand.white)};

  .theme-apl & {
    ${setCssVar('accentColor', brand.apl.green)};
    ${setCssVar('bgColor', rgba(brand.black, 0.75))};
    ${setCssVar('bgColorAlt', brand.grey)};
    ${setCssVar('fgColor', brand.white)};
  }

  .theme-hh & {
    ${setCssVar('accentColor', brand.hortHouse.green)};
    ${setCssVar('bgColor', rgba(brand.black, 0.75))};
    ${setCssVar('bgColorAlt', brand.greyLight)};
    ${setCssVar('fgColor', brand.white)};
  }

  .theme-hta & {
    ${setCssVar('buttonBgColor', brand.white)};
    ${setCssVar('buttonBorderColor', brand.white)};
    ${setCssVar('buttonColor', brand.black)};
  }

  &[data-is-boxed='true'] { 
    position: relative;

    @media ${until(Device.Tablet)} {
      ${SiteWideStyles.Container} {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
`;
const BgImage = styled.img `
  object-fit: contain;
  height: 100%;
  width: 100%;

  @media ${until(Device.Tablet)} {
    [data-is-boxed='true'] & { 
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      object-fit: cover;
      object-position: center center;
      width: 100%;
    }
  }

  @media ${from(Device.Tablet)} {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    width: 100%;
  }
`;
const Inner = styled.div `
  position: relative;
  display: grid;
  background: ${cssVar('accentColor')};

  @media ${until(Device.Tablet)} {
    align-items: center;
    border-radius: 20px 20px 0 0;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    overflow: hidden;
    background: transparent;

    [data-is-boxed='true'] & { 
      grid-template-rows: auto auto;
      padding: 81px 18px 26px;
      border-radius: 0;
    }
  }

  @media ${from(Device.Tablet)} {
    align-items: center;
    border-radius: 20px;
    gap: 30px;
    grid-template-columns: repeat(12, 1fr);
    padding: 96px 0;

    .theme-hta & {
      border: 4px solid ${brand.htaRebrand.greenAlt};
      overflow: hidden;
    }

    [data-is-boxed='true'] & { 
      border: none;
      border-radius: 0;
      background: transparent;
    }
  }
`;
const ImageWrapper = styled.div `
  position: relative;

  [data-is-boxed='true'] & { 
    height: calc(100% + 74px);
    grid-column: 6 / span 6;
    grid-row: 1;
    display: grid;
    align-items: center;
    justify-content: center;
    background-color: white;
    border: 3px solid ${cssVar('bgColor')};
    border-radius: 24px;
    padding: 26px 15%;
  }

  @media ${until(Device.Tablet)} {
    grid-column: 1;
    grid-row: 1;
    background: ${cssVar('accentColor')};
    text-align: center;

    [data-is-boxed='true'] & { 
      height: calc(100% + 54px);
      grid-column: 1;
      grid-row: 1;
      padding: 30px 18px;
    }
  }

  @media ${from(Device.Tablet)} {
    grid-column: 8 / span 4;
    grid-row: 1;

    [data-is-boxed='true'] & { 
      grid-column: 7 / span 5;
    }
  }

  @media ${from(Device.TabletLarge)} {
    [data-is-boxed='true'] & { 
      grid-column: 7 / span 5;
    }
  }

  @media ${from(Device.Desktop)} {
    [data-is-boxed='true'] & { 
      grid-column: 6 / span 6;
    }
  }
`;
const FgImage = styled.img `
  min-height: 214px;

  [data-is-boxed='true'] & { 
    min-height: 100px;
    max-height: 400px;
  }
`;
const ContentWrapper = styled.div `
  position: relative;
  background: ${cssVar('bgColor')};
  color: ${cssVar('fgColor')};

  @media ${until(Device.Tablet)} {
    border-radius: 0 0 20px 20px;
    grid-column: 1;
    grid-row: 2;
    padding: 10px 10px 16px;
    background: ${cssVar('bgColorAlt')};

    [data-is-boxed='true'] & { 
      border-radius: 24px;
      margin: 0 14px;
      padding: 24px 16px;
    }
  }

  @media ${from(Device.Tablet)} {
    border-radius: 20px;
    overflow: hidden;
    grid-column: 2 / span 6;
    grid-row: 1;
    padding: 32px 32px 64px;

    .theme-hta & {
      padding: 32px;
    }
  }

  @media ${from(Device.Desktop)} {
    grid-column: 2 / span 5;
  }
`;
const Title = styled.h2 `
  ${HeadingStyles.H3Styles}

  @media ${until(Device.Tablet)} {
    font-size: 24px;
    line-height: 32px;
  }
`;
const Content = styled.div `
  ${RichTextStyles.contentStyles};
  margin: 0 0 16px;

  ul {
    li {
      &::before {
        background-color: ${brand.white};
      }
    }
  }
`;
const CtaWrapper = styled.div ``;
const EmailContent = styled.span `
  ${fonts.sourceSansPro.regular}
  font-size: 16px;
  line-height: 26px;
  flex: 1 1 auto;

  @media ${from(Device.Tablet)} {
    font-size: 18px;
    line-height: 28px;
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 22px;
    line-height: 34px;
  }
`;
const MailIcon = styled(SvgEnvelope) `
  width: 30px;
  margin-right: 10px;
  color: ${cssVar('accentColor')};

  @media ${from(Device.Tablet)} {
    width: 20px;
    margin-right: 5px;
  }

  @media ${from(Device.TabletLarge)} {
    width: 40px;
    margin-right: 15px;
  }
`;
const EmailArea = styled.a `
  display: flex;
  align-items: center;
  margin: 30px 0;
  color: ${brand.white};
  text-decoration: none;

  &:focus {
    outline: none;
  }

  &:hover,
  &:focus {
    ${EmailContent} {
      text-decoration: underline;
    }
  }

  .theme-hta & {
    margin: 10px 0;
  }
`;
const LinkArea = styled.div `
  display: flex;
  gap: 10px;
  flex-direction: column;

  @media ${from(Device.Tablet)} {
    .theme-hta & {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }
`;
export default {
    BgImage,
    Container,
    Content,
    ContentWrapper,
    CtaWrapper,
    EmailArea,
    EmailContent,
    FgImage,
    ImageWrapper,
    Inner,
    LinkArea,
    MailIcon,
    Title,
};
